import React, { forwardRef, useId } from "react";
import { cn } from "../../utils/cn";

/**
 * DESIGN.md text input.
 *  - 44px height
 *  - 12x16 padding
 *  - 6px (rounded-sm) radius
 *  - 1px hairline border
 *  - focus: 1px info-border (#458fff)
 *  - error: 1px coral border + helper text
 */
export const TextInput = forwardRef(function TextInput(
  {
    label,
    helperText,
    error,
    leadingIcon,
    trailingIcon,
    className,
    inputClassName,
    containerClassName,
    id: idProp,
    type = "text",
    disabled,
    multiline = false,
    rows = 4,
    ...rest
  },
  ref
) {
  const reactId = useId();
  const id = idProp || `input-${reactId}`;

  const wrapperClass = cn(
    "flex items-center gap-2 bg-canvas border rounded-sm transition-colors",
    error
      ? "border-signature-coral focus-within:border-signature-coral"
      : "border-hairline focus-within:border-info-border",
    disabled && "bg-surface-soft opacity-70 cursor-not-allowed",
    !multiline && "h-11",
    "px-4",
    inputClassName
  );

  const fieldBase =
    "w-full bg-transparent outline-none text-body-md text-ink placeholder:text-muted disabled:cursor-not-allowed";

  const Field = multiline ? "textarea" : "input";

  return (
    <div className={cn("flex flex-col gap-1.5", containerClassName)}>
      {label ? (
        <label
          htmlFor={id}
          className="text-body-md font-medium text-ink"
        >
          {label}
        </label>
      ) : null}
      <div className={wrapperClass}>
        {leadingIcon ? (
          <span className="inline-flex shrink-0 text-muted">{leadingIcon}</span>
        ) : null}
        <Field
          ref={ref}
          id={id}
          type={multiline ? undefined : type}
          rows={multiline ? rows : undefined}
          disabled={disabled}
          className={cn(fieldBase, multiline && "py-3 resize-y", className)}
          {...rest}
        />
        {trailingIcon ? (
          <span className="inline-flex shrink-0 text-muted">{trailingIcon}</span>
        ) : null}
      </div>
      {error ? (
        <span className="text-body-md text-signature-coral">{error}</span>
      ) : helperText ? (
        <span className="text-body-md text-muted">{helperText}</span>
      ) : null}
    </div>
  );
});

export default TextInput;
